import React, { useEffect, useMemo, useState } from "react";
import {
  myTasksDeviationReportTriggersListName,
  myTasksSDCAListName,
  myTasksTriggersListName,
} from "src/api/config/myTasksLists.config";
import { Breadcrumbs } from "src/components/Breadcrumb";
import { ReportIcon } from "src/components/ui-icons/sidebar";
import { useAppDispatch } from "src/hooks/redux";
import { LayoutWithAsideAndContent } from "src/pages/LayoutWithAsideAndContent";
import { FlexCol, FlexColCenter, FlexRow, FlexRowCenter } from "src/utils/flexLayouts";
import CheckmarkIcon from "./icon/CheckmarkIcon";
import ShieldSmallIcon from "./icon/ShieldSmallIcon";
import {
  FiveWayKeyType,
  FiveWayType,
  InstrumentsType,
  MatrixItemType,
  MatrixType,
  PartialInstrumentsType,
  SDCAFiveWayType,
  SDCAType,
  SubmitFiveWhyReportFiltersType,
} from "./types";
import MatrixReportFilters from "./MatrixReportFilter";
import { myTasks5WayListName, myTasksSDCA5WayListName } from "src/api/config/myTasks5Way.config";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import useDebounce from "src/hooks/useDobounce";
import { getItemsRequest } from "src/modules/spItems/api";
import { cloneDeep, keyBy } from "lodash-es";
import { Modal } from "src/components/DialogComponent";
import useModalState from "src/hooks/useModalState";
import FiveWhyModalTable from "./FiveWhyModalTable";
import FiveWhyAndDeviationModalTable, { FiveWhyAndDeviationTableHeadType } from "./FiveWhyAndDeviationTable";
import { LookupFields } from "src/modules/modules.type";
import { CheckBoxListItem, LookUpRecord, RadioBoxListItem, UserListItem } from "src/types/main";
import { myTaskActionsListName } from "src/api/config/myTasksActions.config";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Loading from "src/components/Loading";
import type { DateRangeType, DateValueType } from "react-tailwindcss-datepicker";
import moment, { isDate } from "moment";
import { chunkArray } from "src/utils/array.utils";
import { EmptyList } from "src/components/EmptyList";
import { fiveWhyStatus, FiveWhyType } from "src/api/config/systemSwitch";

const submitFiltersData: SubmitFiveWhyReportFiltersType = {
  actionsPlanned: { id: 0, title: "Не выбрано", type: "null", value: null },
  isRootProblemIdentify: { id: 0, title: "Не выбрано", type: "null", value: null },
  isReactPlanAccept: { id: 0, title: "Не выбрано", type: "null", value: null },
  repeatProblem: { id: 0, title: "Не выбрано", type: "null", value: null },
  author: [],
  kpi: [],
  sectors: [],
  departments: [],
  locations: [],
  createDate: {
    startDate: moment().utc(true).startOf("day").toDate(),
    endDate: moment().utc(true).endOf("day").toDate(),
  },
  deviationParticipants: [],
  deviationStatuses: [],
  deviationTriggers: [],
  fiveWhyResponsible: [],
  fiveWhyStatuses: [],
  fiveWhyTriggers: [],
};

const MatrixReport: React.FC = () => {
  const [sortedField, setSortedField] = useState("trigger");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("asc");

  const [submitFilters, setSubmitFilters] = useState<SubmitFiveWhyReportFiltersType>(submitFiltersData);

  function resetSubmitFilters() {
    setSubmitFilters(submitFiltersData);
  }

  const handleSort = (field: string) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc",
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  return (
    <LayoutWithAsideAndContent
      aside={
        <MatrixReportFilters
          changeSubmitFilters={(e) => setSubmitFilters(e)}
          resetSubmitFilters={resetSubmitFilters}
          submitFilters={submitFilters}
        />
      }
      content={
        <FlexCol className="w-full">
          <FlexRow className="px-7.5 h-[76px] border-b justify-between items-center shrink-0">
            <FlexCol>
              <Breadcrumbs
                items={[
                  {
                    name: "Отчеты",
                    icon: <ReportIcon />,
                  },
                  {
                    name: "Матрица SDCA",
                    icon: <ShieldSmallIcon />,
                  },
                ]}
              />
            </FlexCol>
          </FlexRow>
          <FlexRow className="p-7.5">
            <Matrix submitFilters={submitFilters} />
          </FlexRow>
        </FlexCol>
      }
    />
  );
};

export default MatrixReport;

function isLookupFields(obj: any): obj is LookupFields {
  return typeof obj.get_lookupId === "function";
}

function createSDCAMap(items: SDCAFiveWayType[]) {
  // Создаём Map для быстрого поиска
  const myTasksSDCA5WayMap = items.reduce(
    (acc, cur) => {
      const fiveWayKey = isLookupFields(cur._x0035_Way)
        ? cur._x0035_Way.get_lookupId().toString()
        : (cur._x0035_Way as LookUpRecord).ID.toString();

      if (acc.has(fiveWayKey)) {
        const fiveWayValueArray = acc.get(fiveWayKey) ?? [];
        fiveWayValueArray.push(cur);
        acc.set(fiveWayKey, fiveWayValueArray);
      } else {
        acc.set(fiveWayKey, [cur]);
      }

      return acc;
    },
    new Map() as Map<string, SDCAFiveWayType[]>,
  );

  return myTasksSDCA5WayMap;
}

type FilterKeyMap = Record<keyof SubmitFiveWhyReportFiltersType, string>;

const submitFiltersDataKeyMap: FilterKeyMap = {
  locations: "Location",
  departments: "Department",
  sectors: "Region",
  author: "AuthorEmail",
  createDate: "Created",
  fiveWhyStatuses: "Status",
  deviationStatuses: "Status",
  repeatProblem: "ProblemRepeat",
  deviationParticipants: "Editors",
  fiveWhyResponsible: "Editors",
  deviationTriggers: "Trigger",
  fiveWhyTriggers: "Trigger",
  isReactPlanAccept: "Plan",
  kpi: "KPI",

  isRootProblemIdentify: "Draft",
  actionsPlanned: "Draft",
} as const;

function isRadioBoxListItem(obj: any): obj is RadioBoxListItem {
  return obj && typeof obj === "object" && "value" in obj && "title" in obj;
}

function getFiltersDataByFiveWay(filters: SubmitFiveWhyReportFiltersType) {
  const newFiltersObject: Partial<Record<(typeof submitFiltersDataKeyMap)[keyof typeof submitFiltersDataKeyMap], any>> =
    {};

  for (const filter of Object.entries(filters)) {
    const [filterKey, filterValue] = filter as [
      keyof SubmitFiveWhyReportFiltersType,
      CheckBoxListItem[] | UserListItem[] | DateValueType,
    ];

    if (!filterValue) continue;

    // Убедимся, что ключ существует в карте
    if (filterKey in submitFiltersDataKeyMap) {
      const mappedKey = submitFiltersDataKeyMap[
        filterKey
      ] as (typeof submitFiltersDataKeyMap)[keyof typeof submitFiltersDataKeyMap];

      if (filterKey === "author" || filterKey === "fiveWhyResponsible" || filterKey === "deviationParticipants") {
        if ((filterValue as UserListItem[]).length) {
          newFiltersObject[mappedKey] = (filterValue as UserListItem[]).map((f) => f.email.toLowerCase() + "*");
        }
      } else if (filterKey === "createDate") {
        if (filterValue && (filterValue as DateRangeType).startDate && (filterValue as DateRangeType).endDate) {
          const dateObj = filterValue as DateRangeType;

          const dateFilterObject: {
            start: any;
            end: any;
          } = {
            start: null,
            end: null,
          };

          if (typeof dateObj.startDate === "string" && typeof dateObj.endDate === "string") {
            const startDate = moment.utc(dateObj.startDate, true).startOf("day").toDate();
            const endDate = moment.utc(dateObj.endDate, true).endOf("day").toDate();

            dateFilterObject.start = startDate;
            dateFilterObject.end = endDate;
          } else if (isDate(dateObj.startDate) && isDate(dateObj.endDate)) {
            dateFilterObject.start = dateObj.startDate;
            dateFilterObject.end = dateObj.endDate;
          }

          if (dateFilterObject.start && dateFilterObject.end) {
            newFiltersObject["Date5Way"] = dateFilterObject;
          }
        }
      } else if (filterKey === "locations" || filterKey === "departments" || filterKey === "sectors") {
        if ((filterValue as CheckBoxListItem[]).length) {
          newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
        }
      } else if (
        (filterKey === "deviationStatuses" || filterKey === "fiveWhyStatuses") &&
        (filterValue as CheckBoxListItem[]).length
      ) {
        if (filterKey === "deviationStatuses") {
          newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
          newFiltersObject["Type5Way"] = "DeviationReport";
        }

        if (filterKey === "fiveWhyStatuses") {
          newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
          newFiltersObject["Type5Way"] = FiveWhyType;
        }
      } else if (
        (filterKey === "fiveWhyTriggers" || filterKey === "deviationTriggers") &&
        (filterValue as CheckBoxListItem[]).length
      ) {
        if (filterKey === "deviationTriggers") {
          newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
          newFiltersObject["Type5Way"] = "DeviationReport";
        }

        if (filterKey === "fiveWhyTriggers") {
          newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
          newFiltersObject["Type5Way"] = FiveWhyType;
        }
      } else if (filterKey === "kpi" && (filterValue as CheckBoxListItem[]).length) {
        newFiltersObject[mappedKey] = (filterValue as CheckBoxListItem[]).map((f) => f.id);
      } else if (filterKey === "repeatProblem" && "value" in filterValue) {
        if (filterValue.value !== null) {
          newFiltersObject[mappedKey] = filterValue.value;
        }
      } else if (filterKey === "isReactPlanAccept" && isRadioBoxListItem(filterValue)) {
        if (filterValue.value !== null) {
          newFiltersObject[mappedKey] = (filterValue as RadioBoxListItem).title;
        }
      } else if (filterKey === "isRootProblemIdentify" && isRadioBoxListItem(filterValue)) {
        if (filterValue.value !== null) {
          newFiltersObject[mappedKey] = (filterValue as RadioBoxListItem).id === 1 ? '"root":true' : '"root":false';
        }
      }
    }
  }
  return newFiltersObject;
}

const findLastRoot = (arr: any[]) => {
  const stack = [...arr]; // Копируем массив в стек
  let lastRoot = null;

  while (stack.length) {
    const node = stack.pop(); // Достаем последний элемент

    if (node.root === true) {
      lastRoot = node; // Обновляем последний найденный root:true
    }

    if (node.children?.length) {
      stack.push(...node.children); // Добавляем детей в стек
    }
  }

  return lastRoot;
};

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error && typeof error === "object") {
    const err = error as { name?: string; code?: string };
    return err.name === "CanceledError" || err.code === "ERR_CANCELED";
  }
  return false;
}

async function filterFiveWay(fiveWay: FiveWayType[], has: boolean = true, signal?: AbortSignal) {
  return (
    await Promise.all(
      fiveWay.map(async (fiveWayItem) => {
        // 1️⃣ Запрос в SDCA
        const { items: SDCA5WayItems } = await getItemsRequest({
          listName: myTasksSDCA5WayListName,
          filters: { _x0035_Way: [fiveWayItem.ID] },
          signal,
        });

        if (!SDCA5WayItems.length) return false; // Если нет данных в SDCA — отбрасываем

        // 2️⃣ Получаем ID из SDCA

        const tag = fiveWayItem.Type5Way === FiveWhyType ? "#5Почему" : "#Отчет_об_отклонениях";
        const tagItems = SDCA5WayItems.map((item) => `${tag}_${item.ID}*`);

        // 3️⃣ Запрос в Actions
        const { items: myTaskActions } = await getItemsRequest({
          listName: myTaskActionsListName,
          filters: {
            ImportantHashTag: tagItems,
          },
          signal,
        });

        const tagQueryIds = SDCA5WayItems.map((item) => `${tag}_${item.ID}`);

        const result = filterActionsByTags(myTaskActions, tagQueryIds);

        if (has) {
          return result.length > 0;
        } else {
          return !result.length;
        }
      }),
    )
  ).reduce((acc, keep, idx) => (keep ? [...acc, fiveWay[idx]] : acc), [] as FiveWayType[]);
}

function Matrix({ submitFilters }: { submitFilters: SubmitFiveWhyReportFiltersType }) {
  const [instruments, setInstruments] = useState<PartialInstrumentsType>({});
  const [matrix, setMatrix] = useState<MatrixType>(new Map());
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");
  // Map для быстрого поиска связей FiveWay-SDCA: ключ = FiveWayID, значение = массив связей
  // Сохраняем в state чтобы передавать в модалку без зависимости от Redux
  const [sdca5WayMap, setSdca5WayMap] = useState<Map<string, SDCAFiveWayType[]>>(new Map());

  const totalMatrixState: MatrixItemType = {
    item: {},
    DeviationReport: { all: [], done: [] },
    total: [],
    triggerName: "",
    [FiveWhyType as FiveWayKeyType]: { all: [], done: [] },
    ...(cloneDeep(instruments) as any),
  };

  const [total, setTotal] = useState<MatrixItemType>(totalMatrixState);
  const debouncedFilters = useDebounce(submitFilters, 300);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadMatrix = async () => {
      setLoadingMatrix(true);
      try {
        // ============================================
        // ШАГ 1: Загрузка и фильтрация данных FiveWay
        // ============================================

        // Преобразуем фильтры из формы в формат для API запроса
        const fiveWayFilters = getFiltersDataByFiveWay(debouncedFilters);

        // Загружаем основные данные FiveWay с применением фильтров
        const { items: fiveWayItems } = await getItemsRequest({
          listName: myTasks5WayListName,
          filters: fiveWayFilters,
          signal,
        });
        let fiveWay = modifiedItemsState(fiveWayItems, myTasks5WayListName);

        // Фильтр по ответственному лицу: проверяем последнего редактора в списке Editors
        if (!!debouncedFilters.fiveWhyResponsible.length) {
          const emails = debouncedFilters.fiveWhyResponsible.map((f) => f.email.trim());

          fiveWay = fiveWay.filter((fiveWayItem) => {
            const editors = fiveWayItem.Editors.split(";");

            // Для DeviationReport без редакторов - исключаем
            if (!editors.length && fiveWayItem.Type5Way === "DeviationReport") return false;

            // Проверяем, есть ли последний редактор в списке выбранных email
            return emails.includes(editors[editors.length - 1]);
          });
        }

        // Фильтр по идентификации корневой проблемы
        if (debouncedFilters.isRootProblemIdentify.value !== null) {
          if (debouncedFilters.isRootProblemIdentify.value) {
            // Показываем только те, где найдена корневая проблема (root: true)
            fiveWay = fiveWay.filter((fiveWayItem) => {
              const jsonArray = fiveWayItem.Tree ? JSON.parse(fiveWayItem.Tree) : [];

              if (!jsonArray.length) return false;

              // Ищем последний элемент с root: true
              return findLastRoot(jsonArray);
            });
          } else {
            // Показываем только те, где НЕТ корневой проблемы
            fiveWay = fiveWay.filter((fiveWayItem) => {
              const string = !!fiveWayItem.Tree && !!fiveWayItem.Tree.length ? fiveWayItem.Tree : "";

              if (!string.length) return false;

              // Исключаем элементы, содержащие root: true
              return !string.includes('"root":true');
            });
          }
        }

        // Фильтр по наличию запланированных действий
        if (debouncedFilters.actionsPlanned.value !== null) {
          if (debouncedFilters.actionsPlanned.value) {
            // Показываем только те, где есть запланированные действия
            fiveWay = await filterFiveWay(fiveWay, true, signal);
          } else {
            // Показываем только те, где НЕТ запланированных действий
            fiveWay = await filterFiveWay(fiveWay, false, signal);
          }
        }

        // ============================================
        // ШАГ 2: Загрузка справочных данных
        // ============================================

        // Загружаем триггеры для обычных FiveWay
        const { items: TriggerItems } = await getItemsRequest({
          listName: myTasksTriggersListName,
          signal,
        });
        const triggers = modifiedItemsState(TriggerItems, myTasksTriggersListName);
        const triggersObj = keyBy(triggers, "ID"); // Преобразуем в объект для быстрого поиска по ID

        // Загружаем триггеры для DeviationReport
        const { items: DeviationTriggerItems } = await getItemsRequest({
          listName: myTasksDeviationReportTriggersListName,
          signal,
        });

        const deviationTriggers = modifiedItemsState(DeviationTriggerItems, myTasksDeviationReportTriggersListName);
        const deviationTriggersObj = keyBy(deviationTriggers, "ID");

        // Загружаем все SDCA (инструменты) и группируем их по SDCAGroup
        const { items: sdcaItems } = await getItemsRequest({ listName: myTasksSDCAListName, signal });
        const sdca = modifiedItemsState(sdcaItems, myTasksSDCAListName);
        const sdcaObj = keyBy(sdca, "ID"); // Объект для быстрого поиска SDCA по ID

        // Группируем SDCA по группам (SDCAGroup) - каждая группа содержит массив инструментов
        // Каждый инструмент имеет структуру: { item: SDCA, all: [], done: [] }
        const instrumentsObj = sdca.reduce((acc, cur) => {
          const key = cur.SDCAGroup;

          if (!acc[key]) {
            acc[key] = [{ item: cur, all: [], done: [] }];
          } else {
            acc[key].push({ item: cur, all: [], done: [] });
          }

          return acc;
        }, {} as PartialInstrumentsType);

        // ============================================
        // ШАГ 3: Подготовка структур данных для матрицы
        // ============================================

        // Map для хранения данных матрицы: ключ = "TriggerID,KPIID", значение = объект с данными
        const map = new Map();

        // Объект для итоговых данных по всем триггерам
        const totalObj: any = {
          [FiveWhyType]: { all: [], done: [] }, // Все FiveWay
          DeviationReport: { all: [], done: [] }, // Все DeviationReport
          ...cloneDeep(instrumentsObj), // Копия всех групп SDCA
        };

        // Получаем все ID FiveWay элементов для загрузки связей с SDCA
        const fiveWayIds = fiveWay.map((item) => item.ID);

        // Если нет данных FiveWay - завершаем выполнение с пустыми данными
        if (!fiveWayItems.length) {
          if (signal.aborted) return;
          setMatrix(map);
          setTotal(totalObj);
          setInstruments(instrumentsObj);
          return;
        }

        // Разбиваем массив ID на чанки по 20 элементов для оптимизации запросов
        const fiveWayIdsChunks = chunkArray(fiveWayIds, 20);

        // Map для быстрого поиска связей FiveWay-SDCA: ключ = FiveWayID, значение = массив связей
        const myTasksSDCA5WayMap: Map<string, SDCAFiveWayType[]> = new Map();

        // ============================================
        // ШАГ 4: Загрузка связей FiveWay-SDCA батчами
        // ============================================

        for (const fiveWayIdsChunk of fiveWayIdsChunks) {
          // Загружаем связи для текущего чанка ID
          const {
            items: myTasksSDCA5WayItems,
            count,
            nextPage,
          } = await getItemsRequest({
            listName: myTasksSDCA5WayListName,
            filters: { _x0035_Way: fiveWayIdsChunk },
            signal,
          });

          // Создаём Map для быстрого поиска связей по FiveWay ID
          const myTasksSDCA5WayMapChunk = createSDCAMap(myTasksSDCA5WayItems);

          // Объединяем результаты в общий Map
          myTasksSDCA5WayMapChunk.forEach((entry, key) => {
            myTasksSDCA5WayMap.set(key, entry);
          });
        }
        // Сохраняем Map связей FiveWay-SDCA в state для передачи в модалку
        if (!signal.aborted) {
          setSdca5WayMap(myTasksSDCA5WayMap);
        }
        // ============================================
        // ШАГ 5: Построение матрицы данных
        // ============================================

        for (const fiveWayItem of fiveWay) {
          // Создаём уникальный ключ для группировки: "TriggerID,KPIID"
          const key = `${fiveWayItem.Trigger},${fiveWayItem.KPI.ID}`;

          // Получаем или создаём объект для текущей комбинации Trigger+KPI
          const triggerObj = map.get(key) || {
            item: fiveWayItem,
            total: [], // Все элементы для этого триггера+KPI
            kpiName: fiveWayItem.KPI.Title,
            triggerName:
              // Определяем название триггера в зависимости от типа FiveWay
              fiveWayItem.Type5Way === FiveWhyType
                ? (triggersObj[fiveWayItem.Trigger] && triggersObj[fiveWayItem.Trigger].Title) || ""
                : (deviationTriggersObj[fiveWayItem.Trigger] && deviationTriggersObj[fiveWayItem.Trigger].Title) || "",
            [FiveWhyType]: { all: [], done: [] }, // FiveWay элементы
            DeviationReport: { all: [], done: [] }, // DeviationReport элементы
            ...cloneDeep(instrumentsObj), // Копия всех групп SDCA для этого триггера
          };

          // Определяем статус: "2" = завершено (done), иначе - в работе (all)
          const statusKey = fiveWayItem.Status?.ID?.toString() === fiveWhyStatus.closed.toString() ? "done" : "all";

          // Получаем все связи FiveWay-SDCA для текущего элемента
          const myTasksSDCA5WayItems = myTasksSDCA5WayMap.get(fiveWayItem.ID.toString());

          // Если есть связи с SDCA - распределяем элемент по соответствующим группам
          if (myTasksSDCA5WayItems) {
            for (const myTasksSDCA5WayItem of myTasksSDCA5WayItems) {
              const sdcaLookupId = myTasksSDCA5WayItem.SDCA.get_lookupId().toString();

              // Получаем группу SDCA для текущей связи
              const SDCAGroup = sdcaObj[sdcaLookupId].SDCAGroup;

              // Находим объект группы SDCA в структуре триггера
              const SDCAGroupObj = triggerObj[SDCAGroup].find(
                (f: { item: SDCAType; all: FiveWayType[]; done: FiveWayType[] }) =>
                  f.item.ID.toString() === myTasksSDCA5WayItem.SDCA.get_lookupId().toString(),
              );

              // Находим объект группы SDCA в итоговой структуре
              const totalSDCAGroupObj = totalObj[SDCAGroup].find(
                (f: { item: SDCAType; all: FiveWayType[]; done: FiveWayType[] }) =>
                  f.item.ID.toString() === myTasksSDCA5WayItem.SDCA.get_lookupId().toString(),
              );

              // Если элемент завершён - добавляем в массив "done"
              if (statusKey === "done") {
                SDCAGroupObj["done"].push(fiveWayItem);
                totalSDCAGroupObj["done"].push(fiveWayItem);
              }

              // Всегда добавляем в массив "all" (все элементы, независимо от статуса)
              SDCAGroupObj["all"].push(fiveWayItem);
              totalSDCAGroupObj["all"].push(fiveWayItem);
            }
          }

          // Добавляем элемент в общий список для этого триггера+KPI
          triggerObj.total.push(fiveWayItem);

          // Распределяем элемент по типу (FiveWhyType или DeviationReport)
          if (statusKey === "done") {
            // Если завершён - добавляем в "done"
            triggerObj[fiveWayItem.Type5Way as FiveWayKeyType]["done"].push(fiveWayItem);
            totalObj[fiveWayItem.Type5Way]["done"].push(fiveWayItem);
          }

          // Всегда добавляем в "all" для соответствующего типа
          triggerObj[fiveWayItem.Type5Way as FiveWayKeyType]["all"].push(fiveWayItem);
          totalObj[fiveWayItem.Type5Way]["all"].push(fiveWayItem);

          // Сохраняем обновлённый объект обратно в Map
          map.set(key, triggerObj);
        }

        // ============================================
        // ШАГ 6: Сохранение результатов в state
        // ============================================

        if (signal.aborted) return;

        // Сохраняем матрицу только если есть данные
        if (map.size) {
          setMatrix(map);
        }

        // Сохраняем итоговые данные и инструменты
        setTotal(totalObj);
        setInstruments(instrumentsObj);
      } catch (error) {
        if (signal.aborted || isAbortError(error)) return;
        console.error(error);
      } finally {
        if (!signal.aborted) {
          setLoadingMatrix(false);
        }
      }
    };

    loadMatrix();

    return () => controller.abort();
  }, [debouncedFilters]);

  const handleSort = () => {
    setSortDirection((prevDirection) =>
      prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc",
    );
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!matrix || sortDirection === "default") {
      return [...matrix.entries()];
    }

    const sortedData = [...matrix.entries()];

    const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

    sortedData.sort((a, b) => {
      const valueA = a[1]["triggerName"];
      const valueB = b[1]["triggerName"];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB, locales, options)
          : valueB.localeCompare(valueA, locales, options);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortDirection, matrix]) as [string, MatrixItemType][];

  return (
    <FlexCol className="w-full max-h-[700px]">
      <FlexRow className="overflow-auto h-full bg-white rounded-lg table-custom shadow-custom scrollbar-tg">
        <FlexCol className="flex-1 relative divide-y text-[14px] bg-white min-h-fit">
          {/* Header */}
          <FlexRow className="sticky top-0 z-10 font-medium text-black bg-white border-b border-black divide-x">
            <FlexCol className="min-w-[380px] flex-1 divide-y sticky left-0 border-r bg-white">
              <FlexRow className="items-center h-[98px] px-5">Матрица SDCA</FlexRow>
              <FlexRow className="items-center h-14 divide-x shrink-0">
                <FlexCol className="justify-center px-5 w-32 h-full shrink-0">KPI</FlexCol>
                <FlexCol className="justify-center px-5 h-full">
                  <FlexRow className="gap-x-2 items-center">
                    <FlexCol>Триггеры</FlexCol>
                    <FlexCol className="w-auto cursor-pointer" onClick={handleSort}>
                      <SortIcon sortDirection={sortDirection} />
                    </FlexCol>
                  </FlexRow>
                </FlexCol>
              </FlexRow>
            </FlexCol>
            <FlexCol className="!border-l-0 divide-y">
              <FlexRowCenter className="p-2.5">Анализ отклонений</FlexRowCenter>
              <FlexRow className="h-full divide-x">
                <FlexCol className="flex-1 divide-y shrink-0 bg-[#FEF3C780] w-[132px]">
                  <FlexRowCenter className="shrink-0 text-center h-14 p-2.5">
                    <span>5Почему</span>
                  </FlexRowCenter>
                  <FlexRow className="h-full divide-x">
                    <FlexColCenter className="flex-1 font-normal text-primaryGray">Все</FlexColCenter>
                    <FlexColCenter className="flex-1">
                      <CheckmarkIcon />
                    </FlexColCenter>
                  </FlexRow>
                </FlexCol>
                <FlexCol className="flex-1 divide-y shrink-0 bg-[#FDE5D880] w-[132px]">
                  <FlexRowCenter className="shrink-0 text-center h-14 p-2.5">
                    <span>Отчеты об отклонении</span>
                  </FlexRowCenter>
                  <FlexRow className="h-full divide-x">
                    <FlexColCenter className="flex-1 font-normal text-primaryGray">Все</FlexColCenter>
                    <FlexColCenter className="flex-1">
                      <CheckmarkIcon />
                    </FlexColCenter>
                  </FlexRow>
                </FlexCol>
              </FlexRow>
            </FlexCol>
            {Object.keys(instruments).map((instrumentKey) => {
              const instrument = instruments[instrumentKey];

              if (!instrument) return null;

              return (
                <FlexCol className="divide-y" key={instrumentKey}>
                  <FlexRowCenter className="p-2.5">{instrumentKey}</FlexRowCenter>
                  <FlexRow className="h-full divide-x">
                    {instrument.map((instrumentItem) => (
                      <FlexCol className="flex-1 divide-y w-[132px]" key={instrumentItem.item.ID}>
                        <FlexRowCenter className="shrink-0 text-center h-14 p-2.5">
                          <span>{instrumentItem.item.Title}</span>
                        </FlexRowCenter>
                        <FlexRow className="h-full divide-x">
                          <FlexColCenter className="flex-1 font-normal text-primaryGray">Все</FlexColCenter>
                          <FlexColCenter className="flex-1">
                            <CheckmarkIcon />
                          </FlexColCenter>
                        </FlexRow>
                      </FlexCol>
                    ))}
                  </FlexRow>
                </FlexCol>
              );
            })}
          </FlexRow>

          {/* Content */}
          <div className="divide-y">
            {loadingMatrix ? (
              <div className="py-5">
                <Loading />
              </div>
            ) : sortedTableBodyContent.length ? (
              sortedTableBodyContent.map(([key, matrixItem]) => (
                <MatrixItem
                  key={key}
                  triggerKpiId={key}
                  matrixItem={matrixItem}
                  instruments={instruments}
                  sdca5WayMap={sdca5WayMap}
                />
              ))
            ) : (
              <div className="py-5">
                <EmptyList title="Нет данных" />
              </div>
            )}
          </div>
          {/* Footer */}
          <FlexRow className="sticky bottom-0 z-10 font-medium text-black bg-white border-t border-black divide-x">
            <FlexCol className="min-w-[380px] flex-1 divide-y sticky left-0 border-r bg-white">
              <FlexRow className="items-center p-5">Всего</FlexRow>
            </FlexCol>
            <FlexCol className="!border-l-0 divide-y">
              <FlexRow className="h-full divide-x bg-[#FEF3C780]">
                <FlexCol className="flex-1 divide-y w-[132px]">
                  <FlexRow className="h-full divide-x">
                    <FlexColCenter className="flex-1">
                      {total[FiveWhyType as FiveWayKeyType].all.length.toString()}
                    </FlexColCenter>
                    <FlexColCenter className="flex-1">
                      {total[FiveWhyType as FiveWayKeyType].done.length.toString()}
                    </FlexColCenter>
                  </FlexRow>
                </FlexCol>
              </FlexRow>
            </FlexCol>
            <FlexCol className="divide-y">
              <FlexRow className="h-full divide-x bg-[#FDE5D880]">
                <FlexCol className="flex-1 divide-y w-[132px]">
                  <FlexRow className="h-full divide-x">
                    <FlexColCenter className="flex-1">{total["DeviationReport"].all.length.toString()}</FlexColCenter>
                    <FlexColCenter className="flex-1">{total["DeviationReport"].done.length.toString()}</FlexColCenter>
                  </FlexRow>
                </FlexCol>
              </FlexRow>
            </FlexCol>
            {Object.keys(instruments).map((instrumentKey) => {
              const instrumentItems = total[instrumentKey];

              if (!instrumentItems.length) return null;

              return (
                <FlexCol className="divide-y" key={instrumentKey}>
                  <FlexRow className="h-full divide-x">
                    {instrumentItems.map((instrumentItem) => (
                      <FlexCol className="divide-y w-[132px]" key={instrumentItem.item.ID}>
                        <FlexRow className="h-full divide-x">
                          <FlexColCenter className="w-1/2">{instrumentItem.all.length.toString() || "0"}</FlexColCenter>
                          <FlexColCenter className="w-1/2">
                            {instrumentItem.done.length.toString() || "0"}
                          </FlexColCenter>
                        </FlexRow>
                      </FlexCol>
                    ))}
                  </FlexRow>
                </FlexCol>
              );
            })}
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </FlexCol>
  );
}

function filterActionsByTags(actions: { ImportantHashTag: string }[], query: string[]) {
  const tagSet = new Set(query); // Set для быстрого поиска

  return actions.filter((action) => {
    // Берем все теги в начале строки (до первого не-тега)
    const tags = action.ImportantHashTag.match(/(^|\s)(#\S+)/g)?.map((tag) => tag.trim()) || [];

    // Проверяем, есть ли среди них точное совпадение с query
    return tags.some((tag) => tagSet.has(tag));
  });
}

function MatrixItem({
  triggerKpiId,
  matrixItem,
  instruments,
  sdca5WayMap,
}: {
  triggerKpiId: string;
  matrixItem: MatrixItemType;
  instruments: Partial<InstrumentsType>;
  sdca5WayMap: Map<string, SDCAFiveWayType[]>;
}) {
  const [fiveWhyOpenModal, toggleFiveWhyOpenModal] = useModalState(false);
  const [loading, setLoading] = useState(false);
  const [fiveWhyType, setFiveWhyType] = useState("");
  const [status, setStatus] = useState("");
  const [list, setList] = useState<
    (Record<FiveWhyAndDeviationTableHeadType | "ID" | "item", any> & { ID: number; item: FiveWayType })[]
  >([]);

  const myTasksSDCAItemsToMap = useMemo(() => {
    return createSDCAMap(Array.from(sdca5WayMap.values()).flat());
  }, [sdca5WayMap]);

  async function openModalByType(type: string, status: string, fiveWayList: FiveWayType[]) {
    toggleFiveWhyOpenModal(true);
    setFiveWhyType(type);
    setStatus(status);

    try {
      setLoading(true);
      const tempList = [] as (Record<FiveWhyAndDeviationTableHeadType | "ID" | "item", any> & {
        ID: number;
        item: FiveWayType;
      })[];

      for (let cur of fiveWayList) {
        const SDCA5WayItems = myTasksSDCAItemsToMap.has(cur.ID.toString())
          ? (myTasksSDCAItemsToMap.get(cur.ID.toString()) ?? [])
          : [];

        const obj = {
          ID: cur.ID,
          Type: cur.Type5Way === FiveWhyType ? "5Почему" : "Отчет об отклонении",
          Title: cur.Title,
          CreatedDate: cur.Created,
          ActionCount: 0,
          InstrumentSDCACount: SDCA5WayItems.length,
          item: cur,
        };

        console.log("SDCA5WayItems", SDCA5WayItems);
        console.log("cur", cur);

        if (!!SDCA5WayItems.length) {
          const tag = cur.Type5Way === FiveWhyType ? "#5Почему" : "#Отчет_об_отклонениях";

          const tagItems = SDCA5WayItems.map((item) => `${tag}_${item.ID}`);

          const { items: myTaskActions } = await getItemsRequest({
            listName: myTaskActionsListName,
            filters: {
              ImportantHashTag: tagItems,
            },
          });

          const tagQueryIds = SDCA5WayItems.map((item) => `${tag}_${item.ID}`);

          const result = filterActionsByTags(myTaskActions, tagQueryIds) || [];

          obj.ActionCount = result.length;
        }

        tempList.push(obj);
      }

      setList(tempList);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  return (
    <FlexRow className="divide-x min-h-[56px]" key={triggerKpiId}>
      <FlexCol className="min-w-[380px] flex-1 divide-y sticky left-0 border-r bg-white">
        <FlexRow className="items-center h-full divide-x">
          <FlexCol className="justify-center px-5 w-32 max-h-14 shrink-0 line-clamp-2" title={matrixItem.kpiName}>
            {matrixItem.kpiName}
          </FlexCol>
          <FlexCol className="justify-center h-full py-2.5 px-5 ">{matrixItem.triggerName}</FlexCol>
        </FlexRow>
      </FlexCol>
      <FlexCol className="divide-y !border-l-0">
        <FlexRow className="h-full divide-x bg-[#FEF3C780]">
          <FlexCol className="divide-y w-[132px]">
            <FlexRow className="h-full divide-x">
              <FlexColCenter className="w-1/2">
                <span
                  className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                  onClick={() => openModalByType(FiveWhyType, "all", matrixItem[FiveWhyType as FiveWayKeyType].all)}
                >
                  {matrixItem[FiveWhyType as FiveWayKeyType].all.length.toString() || "0"}
                </span>
              </FlexColCenter>
              <FlexColCenter className="w-1/2">
                <span
                  className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                  onClick={() => openModalByType(FiveWhyType, "done", matrixItem[FiveWhyType as FiveWayKeyType].done)}
                >
                  {matrixItem[FiveWhyType as FiveWayKeyType].done.length.toString() || "0"}
                </span>
              </FlexColCenter>
            </FlexRow>
          </FlexCol>
        </FlexRow>
      </FlexCol>
      <FlexCol className="divide-y">
        <FlexRow className="h-full divide-x bg-[#FDE5D880]">
          <FlexCol className=" divide-y w-[132px]">
            <FlexRow className="h-full divide-x">
              <FlexColCenter className="w-1/2">
                <span
                  className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                  onClick={() => openModalByType("DeviationReport", "all", matrixItem.DeviationReport.all)}
                >
                  {matrixItem.DeviationReport.all.length.toString() || "0"}
                </span>
              </FlexColCenter>
              <FlexColCenter className="w-1/2">
                <span
                  className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                  onClick={() => openModalByType("DeviationReport", "done", matrixItem.DeviationReport.done)}
                >
                  {matrixItem.DeviationReport.done.length.toString() || "0"}
                </span>
              </FlexColCenter>
            </FlexRow>
          </FlexCol>
        </FlexRow>
      </FlexCol>
      {Object.keys(instruments).map((instrumentKey) => {
        const instrumentItems = matrixItem[instrumentKey];

        if (!instrumentItems.length) return null;

        return (
          <FlexCol className="divide-y" key={instrumentKey}>
            <FlexRow className="h-full divide-x">
              {instrumentItems.map((instrumentItem) => (
                <FlexCol className="divide-y w-[132px]" key={instrumentItem.item.ID}>
                  <FlexRow className="h-full divide-x">
                    <FlexColCenter className="w-1/2">
                      <span
                        className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                        onClick={() => openModalByType(instrumentItem.item.Title, "all", instrumentItem.all)}
                      >
                        {instrumentItem.all.length.toString() || "0"}
                      </span>
                    </FlexColCenter>
                    <FlexColCenter className="w-1/2">
                      <span
                        className="flex justify-center items-center w-full h-full cursor-pointer hover:border hover:border-black"
                        onClick={() => openModalByType(instrumentItem.item.Title, "done", instrumentItem.done)}
                      >
                        {instrumentItem.done.length.toString() || "0"}
                      </span>
                    </FlexColCenter>
                  </FlexRow>
                </FlexCol>
              ))}
            </FlexRow>
          </FlexCol>
        );
      })}

      <Modal open={fiveWhyOpenModal} toggleDialog={toggleFiveWhyOpenModal}>
        <header className="px-10 mb-5">
          <h1 className="mb-4 text-black">
            {fiveWhyType !== "DeviationReport" && fiveWhyType !== FiveWhyType && "5Почему и отчеты об отклонениях"}
            {fiveWhyType === FiveWhyType && "5Почему"}
            {fiveWhyType === "DeviationReport" && "Отчеты об отклонении"}
          </h1>
          <p className="mb-4 flex items-center space-x-[3px]">
            <span>Триггер:</span>
            <span>{matrixItem.triggerName}</span>
          </p>
          {fiveWhyType !== "DeviationReport" && fiveWhyType !== FiveWhyType && (
            <p className="mb-4 flex items-center space-x-[3px]">
              <span>Инструмент:</span>
              <span>{fiveWhyType}</span>
            </p>
          )}
          <p className="flex items-center space-x-[3px]">
            <span>Статус:</span>
            <span className="inline-flex items-center">{status === "all" ? "Все" : "Закрыто"}</span>
          </p>
        </header>

        <div className="px-10">
          {fiveWhyType !== "DeviationReport" && fiveWhyType !== FiveWhyType ? (
            <FiveWhyAndDeviationModalTable list={list} loading={loading} />
          ) : (
            <FiveWhyModalTable
              list={list}
              loading={loading}
              emplyListTitle={fiveWhyType === FiveWhyType ? "Нет 5почему" : "Нет Отчетов об отклонении"}
            />
          )}
        </div>
      </Modal>
    </FlexRow>
  );
}
